import React, { useState, useEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';
import RiveScript from 'rivescript';
import Accordion, { AccordionItem } from '../@base/Accordion';
import Checkbox from '../@base/Checkbox';
import Input from '../@base/Input';
import TextArea from '../@base/TextArea';
import DialogNotification from '../DialogNotification/DialogNotification';
import {
  getParameter, handleInputValid, onValidHandler, preparingInput, Validation,
} from './Commons';

import styles from './SharedStyle.module.css';

const RivescriptComponent = (props) => {
  if (!props.component.parameters) return null;

  const parameterList = props.component.parameters;

  const ut8Enabled = getParameter(parameterList, 'rivescript.utf8');
  const strictEnabled = getParameter(parameterList, 'rivescript.strict');
  const concatMode = getParameter(parameterList, 'rivescript.concat');
  const forceCaseEnabled = getParameter(parameterList, 'rivescript.forcecase');
  const depthEnabled = getParameter(parameterList, 'rivescript.depth');
  const rules = getParameter(parameterList, 'rivescript.rules');
  const throwExceptionsEnabled = getParameter(parameterList, 'rivescript.throwexceptions');
  const unicodePunctuationEnabled = getParameter(parameterList, 'rivescript.unicodepunctuation');

  rules.value = rules.value ? rules.value : '';

  const extraConfigurationExists = !(
    !ut8Enabled && !strictEnabled
    && !concatMode && !forceCaseEnabled
    && !depthEnabled && !throwExceptionsEnabled && !unicodePunctuationEnabled);

  const rivescriptBot = new RiveScript(
    { forceCase: forceCaseEnabled ? forceCaseEnabled.value : true },
  );

  const [showDeprecatedAlert, setShowDeprecatedAlert] = useState(!extraConfigurationExists);
  const [validScript, setValidScript] = useState(true);
  const [inputValid, setInputValid] = useState({});

  useEffect(() => {
    props.setHelperContent(
      <div>
        {ReactHtmlParser(props.component.description)}
      </div>,
    );
  }, []);

  useEffect(() => {
    handleInputValid(inputValid, props);
  }, [inputValid]);

  const handleRivescriptSubmit = (script) => {
    onValidHandler(setInputValid, rules);

    if (!rivescriptBot.stream(script, () => setValidScript(false))) {
      setValidScript(false);
    } else {
      setValidScript(true);
    }
  };

  const handleClickCheckButton = (parameter) => {
    props.updateParameter({
      ...parameter,
      value: `${!(parameter.value === 'true')}`,
    });
  };

  return (
    <>

      {showDeprecatedAlert ? (
        <DialogNotification
          color="danger"
          title={<p>Atenção!</p>}
          content={(
            <div className={styles.Helper}>
              Esse componente Rivescript está desatualizado.
              <br />
              Sugerimos que ele seja removido e que seja adicionado um novo Rivescript.
            </div>
          )}
          okAction={() => setShowDeprecatedAlert(false)}
        />
      ) : null}

      <Accordion>
        <AccordionItem active title={ReactHtmlParser(rules.parameter.hint)}>
          <div>Regras</div>
          <div className={styles.ComponentSubList}>
            <Validation
              isValid={onValidHandler(setInputValid, rules)}
              setInvalid={
                () => ({ valid: validScript, message: 'Código Rivescript está inválido' })
              }
            >
              <TextArea
                height={250}
                placeholder="Digite as regras"
                value={rules.value}
                mandatory={rules.parameter.mandatory}
                info={(
                  <p>
                    As regras devem seguir a sintaxe do
                    {' '}
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://www.rivescript.com/docs/tutorial"
                    >
                      Rivescript
                    </a>
                    , para que o chatbot funcione adequadamente.
                  </p>
                )}
                onChange={(e) => {
                  handleRivescriptSubmit(e.target.value);
                  props.updateParameter({
                    ...rules,
                    value: e.target.value,
                  });
                }}
              />
            </Validation>
          </div>
        </AccordionItem>
        {extraConfigurationExists
          ? (
            <AccordionItem>
              <div>Configurações extras </div>
              <div className={styles.ComponentSubList} style={{ textAlign: 'left' }}>
                <div className="item-list">
                  <div className="row">
                    {preparingInput(<Input
                      label="Limite de profundidade:"
                      labelWidth={200}
                      width={100}
                      inline
                      title={depthEnabled.parameter.hint}
                    />, depthEnabled, props, onValidHandler(setInputValid, depthEnabled))}
                  </div>
                  <div />
                </div>
                <div className="item-list">
                  <div className="row">
                    {preparingInput(<Input
                      label="Modo de concatenação:"
                      labelWidth={200}
                      width={100}
                      inline
                      title={`${concatMode.parameter.hint}. Utilize (NONE, SPACE, NEWLINE)`}
                    />, concatMode, props, onValidHandler(setInputValid, concatMode))}
                  </div>
                  <div />
                </div>

                <div className="item-list">
                  <div className="row">
                    <div style={{ width: 200 }}> Interromper em caso de erro</div>
                    <Checkbox
                      checked={throwExceptionsEnabled.value === 'true'}
                      onClick={() => handleClickCheckButton(throwExceptionsEnabled)}
                      title={`${throwExceptionsEnabled.parameter.hint} Marque para sim`}
                    />
                  </div>
                  <div />
                </div>

                <div className="item-list">
                  <div className="row">
                    <div style={{ width: 200 }}> Utilizar UTF-8</div>
                    <Checkbox
                      checked={ut8Enabled.value === 'true'}
                      onClick={() => handleClickCheckButton(ut8Enabled)}
                      title={`${ut8Enabled.parameter.hint} Marque para sim`}
                    />
                  </div>
                  <div />
                </div>

                <div className="item-list">
                  <div className="row">
                    <div style={{ width: 200 }}> Utilizar sintaxe restrita</div>
                    <Checkbox
                      checked={strictEnabled.value === 'true'}
                      onClick={() => handleClickCheckButton(strictEnabled)}
                      title={`${strictEnabled.parameter.hint} Marque para sim`}
                    />
                  </div>
                  <div />
                </div>

                <div className="item-list">
                  <div className="row">
                    <div style={{ width: 200 }}> Acentuar caracteres especiais</div>
                    <Checkbox
                      checked={unicodePunctuationEnabled.value === 'true'}
                      onClick={() => handleClickCheckButton(unicodePunctuationEnabled)}
                      title={`${unicodePunctuationEnabled.parameter.hint} Marque para sim`}
                    />
                  </div>
                  <div />
                </div>

                <div className="item-list">
                  <div className="row">
                    <div style={{ width: 200 }}> Forçar triggers em caixa baixa</div>
                    <Checkbox
                      checked={forceCaseEnabled.value === 'true'}
                      onClick={() => handleClickCheckButton(forceCaseEnabled)}
                      title={`${forceCaseEnabled.parameter.hint} Marque para sim`}
                    />
                  </div>
                  <div />
                </div>

              </div>
            </AccordionItem>
          ) : <></>}
        {props.localizationAccordionItem || null}
      </Accordion>
    </>
  );
};

export default RivescriptComponent;
